<template>
  <div>
    <Subheader title="My Profile" />

    <div class="app-content-column">
      <div class="card">
        <div class="profile-container">
          <UserProfileImage :profile-pic="user.profilePic" class="profile-picture" />
          <h2>{{ user.fullName }}</h2>
        </div>

        <form @submit.stop.prevent="submitPhoto">
          <h5>Update Profile Picture</h5>
          <input ref="resultFiles" class="input" type="file" @change="previewFile" />
          <div class="button-wrapper is-centered">
            <button class="large-button" type="submit" :disabled="!photo">
              Update photo
            </button>
          </div>
        </form>
      </div>

      <div class="card">
        <div v-if="!showEditProfile">
          <h5>About</h5>
          <p>{{ user.description ? user.description : 'No Profile Provided' }}</p>

          <div v-if="user.facebook" class="social">
            <font-awesome-icon :icon="['fab', 'facebook']" size="2x" class="social__icon" />
            <a :href="user.facebook" class="social__text">{{ user.facebook }}</a>
          </div>
          <div v-if="user.instagram" class="social">
            <font-awesome-icon :icon="['fab', 'instagram']" size="2x" class="social__icon" />
            <a :href="user.instagram" class="social__text">{{ user.instagram }}</a>
          </div>
          <div v-if="user.twitter" class="social">
            <font-awesome-icon :icon="['fab', 'twitter']" size="2x" class="social__icon" />
            <a :href="user.twitter" class="social__text">{{ user.twitter }}</a>
          </div>

          <div class="button-wrapper is-centered">
            <button class="large-button" @click="toggleEditProfile">Edit Description</button>
          </div>
        </div>

        <form v-if="showEditProfile" @submit.stop.prevent="submitProfile">
          <h5>About</h5>
          <Field
            :errors="fieldErrors"
            :show-field-errors="fieldErrors.length > 0"
            name="description"
          >
            <textarea
              v-model="newUserData.description"
              placeholder="Your Bio"
              class="textarea"
              cols="20"
              rows="10"
            />
          </Field>

          <h5>Social Media</h5>
          <div class="form">
            <Field
              :errors="fieldErrors"
              :show-field-errors="fieldErrors.length > 0"
              :form-errors="formErrors"
              name="facebook"
            >
              <input v-model="newUserData.facebook" placeholder="Facebook" />
            </Field>
            <Field
              :errors="fieldErrors"
              :show-field-errors="fieldErrors.length > 0"
              :form-errors="formErrors"
              name="instagram"
            >
              <input v-model="newUserData.instagram" placeholder="Instagram" />
            </Field>
            <Field
              :errors="fieldErrors"
              :show-field-errors="fieldErrors.length > 0"
              :form-errors="formErrors"
              name="twitter"
            >
              <input v-model="newUserData.twitter" placeholder="Twitter" />
            </Field>
          </div>

          <div class="button-wrapper is-centered">
            <button class="large-button" @click="toggleEditProfile">Cancel</button>
            <button type="submit" class="large-button" :disabled="submitting">Save</button>
          </div>
        </form>
      </div>

      <form class="card" @submit.stop.prevent="submitAttributes">
        <h5>Causes and Locations</h5>

        <h4>Causes</h4>
        <div class="button-wrapper">
          <button
            v-for="attr in interestAttrs"
            :key="attr.id"
            type="button"
            class="tag"
            :class="{ 'tag--inverted': attr.selected }"
            @click="toggleAttributes(attr)"
          >
            {{ attr.name }}
          </button>
        </div>

        <h4>Locations</h4>
        <div class="button-wrapper">
          <button
            v-for="attr in locationAttrs"
            :key="attr.id"
            type="button"
            class="tag"
            :class="{ 'tag--inverted': attr.selected }"
            @click="toggleAttributes(attr)"
          >
            {{ attr.name }}
          </button>
        </div>

        <div class="button-wrapper is-centered">
          <button type="submit" class="large-button">
            Update
          </button>
        </div>
      </form>

      <router-link :to="{ name: 'Profile', params: { userId: user.id } }" class="view-link">
        <h5>View How Others See My Profile</h5>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import User from '@/services/users'
import Attribute from '@/services/attributes'

import Subheader from '@/components/Subheader'
import UserProfileImage from '@/components/images/UserProfileImage'
import Field from '@/components/Field'

export default {
  name: 'EditProfile',
  components: {
    Subheader,
    UserProfileImage,
    Field,
  },
  data() {
    return {
      selectedAttrs: [],
      locationAttrs: [],
      interestAttrs: [],
      photo: null,
      newUserData: User.create(),
      showEditProfile: false,
      fieldErrors: [],
      formErrors: {},
      loading: false,
      submitting: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  created() {
    this.getAttributes()
    this.newUserData = User.create(this.user)
  },
  methods: {
    previewFile(event) {
      this.photo = event.target.files[0]
    },
    submitPhoto() {
      User.api.uploadProfilePic(this.user.id, this.photo).then(data => {
        this.$store.commit('UPDATE_USER', data)
        this.$Alert.alert({
          type: 'success',
          message: `<h2>Profile Updated</h2>`,
          timeout: 3000,
        })
        this.photo = null
      })
    },
    async submitProfile() {
      this.submitting = true
      try {
        const fields = ['description', 'facebook', 'instagram', 'twitter']
        const updatedUser = await User.api.update(this.newUserData, fields)
        this.$store.commit('UPDATE_USER', updatedUser)
        this.$Alert.alert({
          type: 'success',
          message: `<h2>Profile Updated</h2>`,
          timeout: 3000,
        })
        this.toggleEditProfile()
      } catch (error) {
        const { response } = error
        if (response && response.data) {
          this.formErrors = response.data
        } else {
          this.$Alert.alert({
            type: 'error',
            message: `<h2>There was an error updating your profile. Please try again later!</h2>`,
            timeout: 4000,
          })
        }
        return
      } finally {
        this.submitting = false
      }
    },
    submitAttributes() {
      const fields = ['interests']
      const newUser = User.create(this.user)
      newUser['interests'] = this.selectedAttrs
      User.api.update(newUser, fields).then(data => {
        this.$store.commit('UPDATE_USER', data)
        this.$Alert.alert({
          type: 'success',
          message: `<h2>Attributes Updated</h2>`,
          timeout: 3000,
        })
      })
    },
    toggleEditProfile() {
      if (!this.showEditProfile) {
        this.newUserData.description = this.user.description
      }
      this.showEditProfile = !this.showEditProfile
    },
    async getAttributes() {
      this.loading = true
      try {
        this.selectedAttrs = this.user.interests
        let attrList = await Attribute.api.list()
        attrList = attrList.results.map(attr => {
          if (this.user.interests.includes(attr.id)) {
            return { ...attr, selected: true }
          } else {
            return { ...attr, selected: false }
          }
        })
        this.locationAttrs = attrList.filter(attr => attr.group === 'Location')
        this.interestAttrs = attrList.filter(attr => attr.group === 'Interest Area')
      } catch {
        this.$Alert.alert({
          type: 'error',
          message: `<h2>There was an error loading this page. Please try again later!</h2>`,
          timeout: 4000,
        })
      } finally {
        this.loading = false
      }
    },
    /* Toggle attribute selection */
    toggleAttributes(attr) {
      if (attr.selected) {
        const remainingAttrs = this.selectedAttrs.filter(e => {
          return e !== attr.id
        })
        this.selectedAttrs = remainingAttrs
        attr.selected = false
      } else if (!attr.selected) {
        this.selectedAttrs.push(attr.id)
        attr.selected = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
}

.view-link {
  display: flex;
  margin: 2rem 1em;
}

.profile-container {
  text-align: center;
}

.profile-picture {
  margin-top: 4.5rem;
}

.social {
  display: flex;
  align-items: center;

  &__icon {
    flex: 1;
    margin: 0.5rem 0.5rem 0.5rem 0;
    color: $primary;
  }

  &__text {
    flex: 10;
    font-family: 'Open Sans', sans-serif;
    font-size: 1rem;
    color: $charcoal;
    font-weight: 200;
    line-height: 1.4rem;
  }
}
</style>
